'use client';

import { useGetFilingCategories,
  useJurisdictions,
} from '@commenda/api';
import { MultiSelect } from '@commenda/kakashi';
import { ObligationRulesCategory } from '@commenda/prisma';
import { IBusinessEntity } from '@commenda/types';
import { ComplianceModel } from '@commenda/utils';
import {
  getMonthNameByNumber,
  MonthIndexOfMonth,
} from '@commenda/utils/datetime';
import { useContext, useMemo } from 'react';
import { LabelWithGuidanceTooltip } from '../../../form-builder/components/wrappers/LabelWithGuidanceTooltip';
import { SelectComponent } from '../../../form-builder/components/wrappers/Select';
import { FormContext } from '../../../form-builder/hooks/FormContext';
import { INTAKE_FIELDS } from './fieldOptions';
import { WizardModuleCard } from './WizardModuleCard';

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="txt-small-regular text-text-tertiary">{label}</span>
    <span className="txt-small-medium text-text-primary">{value || '—'}</span>
  </div>
);

export const Part1Intake = ({
  businessEntity,
  lockedFiscalYear,
}: {
  businessEntity: IBusinessEntity;
  lockedFiscalYear?: number;
}) => {
  const { formData, updateField } = useContext(FormContext);
  const { labelOf } = useJurisdictions();
  const general = businessEntity?.generalCorporationInfo;
  const fyEnd = businessEntity?.accountingInfo?.fiscalYear?.data?.FIXED_DATE;

  const { data: filingCategoriesData } = useGetFilingCategories(
    general?.incorporationCountry,
    general?.jurisdiction
  );

  const fiscalYearOptions = useMemo(() => {
    if (lockedFiscalYear != null) {
      return [
        { value: String(lockedFiscalYear), label: `FY ${lockedFiscalYear}` },
      ];
    }
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => {
      const year = currentYear - 1 + i;
      return { value: String(year), label: `FY ${year}` };
    });
  }, [lockedFiscalYear]);

  const complianceCategoryOptions = useMemo(() => {
    const options = (filingCategoriesData?.categories ?? []).map(
      (category) => ({
        value: category,
        label: ComplianceModel.getFormattedComplianceCategory(
          category,
          general?.incorporationCountry
        ),
      })
    );
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [filingCategoriesData?.categories, general?.incorporationCountry]);

  const selectedCategories =
    (formData[INTAKE_FIELDS.COMPLIANCE_CATEGORIES] as
      | ObligationRulesCategory[]
      | undefined) ?? [];

  return (
    <>
      <WizardModuleCard
        title="Entity details"
        description="These details are used to generate your compliance calendar."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DetailRow label="Legal name" value={general?.legalName} />
          <DetailRow
            label="Jurisdiction"
            value={
              general?.jurisdiction
                ? labelOf(general.jurisdiction)
                : undefined
            }
          />
          <DetailRow
            label="Incorporation country"
            value={general?.incorporationCountry}
          />
          <DetailRow label="Formation date" value={general?.formationDate} />
          <DetailRow
            label="Fiscal-year end"
            value={
              fyEnd?.month != null && fyEnd?.day != null
                ? `${getMonthNameByNumber(MonthIndexOfMonth[fyEnd.month])} ${fyEnd.day}`
                : undefined
            }
          />
        </div>
      </WizardModuleCard>

      <WizardModuleCard
        title="Onboarding details"
        description="Tell us the fiscal year to onboard and any compliance categories to track."
      >
        <SelectComponent
          fieldId={INTAKE_FIELDS.FISCAL_YEAR}
          label="Fiscal year"
          placeholder="Select a fiscal year"
          options={fiscalYearOptions}
          validation={{ rules: { required: true } }}
          props={{ disabled: lockedFiscalYear != null }}
        />

        <div className="mb-4">
          <LabelWithGuidanceTooltip
            label="Compliance categories (optional)"
            description="Select the compliance areas you want us to track for this entity."
          />
          <MultiSelect
            id="compliance-categories"
            options={complianceCategoryOptions}
            defaultValue={selectedCategories}
            onValueChange={(values) =>
              updateField(
                INTAKE_FIELDS.COMPLIANCE_CATEGORIES,
                values as ObligationRulesCategory[]
              )
            }
            placeholder="Choose one or more categories"
            hideSelectAll
          />
        </div>
      </WizardModuleCard>
    </>
  );
};
